<script setup lang="ts">
import { Sketch } from '@ckpack/vue-color'
import { Pipette } from 'lucide-vue-next'

import useColorPicker from '@/components/ui/ColorPicker/useColorPicker'

import FieldRow from './FieldRow.vue'
import ResetButton from './ResetButton.vue'

const props = defineProps<{
  id: string
  label: string
  help?: string
  modelValue: string
  resetLabel?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'update:open': [value: boolean]
  reset: []
}>()

const { t } = useI18n()

const HEX_COLOR = /^#[0-9a-f]{6}$/i

const { isEyeDropperSupported, useEyeDropper } = useColorPicker()

const colorModel = computed({
  get: () => props.modelValue || '#000000',
  set: (value: unknown) => {
    const hex = typeof value === 'string' ? value : ((value as { hex?: string })?.hex ?? '')
    if (!hex) return

    emit('update:modelValue', hex.startsWith('#') ? hex : `#${hex}`)
  }
})

const hexDraft = ref(props.modelValue)

watch(
  () => props.modelValue,
  (value) => {
    hexDraft.value = value
  }
)

const onHexInput = (value: string | number) => {
  const raw = String(value).trim()
  hexDraft.value = raw

  const hex = raw.startsWith('#') ? raw : `#${raw}`
  if (HEX_COLOR.test(hex)) emit('update:modelValue', hex)
}

const onHexBlur = () => {
  hexDraft.value = props.modelValue
}

const pickFromScreen = async () => {
  if (!isEyeDropperSupported.value) return

  try {
    const { sRGBHex } = await useEyeDropper()
    if (sRGBHex) emit('update:modelValue', sRGBHex)
  } catch {
    return
  }
}
</script>

<template>
  <FieldRow :label="label" :help="help" :label-for="id">
    <ResetButton v-if="resetLabel" :label="resetLabel" @click="emit('reset')" />
    <Popover @update:open="emit('update:open', $event)">
      <PopoverTrigger as-child>
        <Button :id="id" type="button" variant="outline" class="h-auto gap-2 py-1.5 pr-2.5 pl-2">
          <span class="border-border size-5 shrink-0 rounded border" :style="{ background: modelValue }" />
          <span class="text-foreground-secondary text-xs font-medium tabular-nums uppercase">{{ modelValue }}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-auto p-3" align="end">
        <Sketch
          v-model="colorModel"
          :disable-alpha="true"
          :disable-fields="true"
          :preset-colors="[]"
          class="sketch-no-presets"
        />
        <div class="mt-3 flex items-center gap-2">
          <Input
            :model-value="hexDraft"
            type="text"
            maxlength="7"
            :aria-label="t('pages.loyaltySupports.webapp.editor.color.hexLabel')"
            class="w-24"
            @update:model-value="onHexInput"
            @blur="onHexBlur"
          />
          <Button
            v-if="isEyeDropperSupported"
            variant="outline"
            size="icon"
            :aria-label="t('pages.loyaltySupports.webapp.editor.color.pickFromScreen')"
            @click="pickFromScreen"
          >
            <Pipette />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  </FieldRow>
</template>

<style scoped>
.sketch-no-presets :deep(.vc-sketch-presets) {
  display: none;
}
</style>
